import type { ColumnTable } from 'arquero';
import * as aq from 'arquero';

import { getNestedValue } from '#core/lib/utils/object.ts';

import { collectColumnRefs } from './column-refs-walker.ts';
import type { PipelineSpec } from './pipeline-schema.ts';

/**
 * True when a column reference uses dot-notation to address a nested field
 * (e.g. `well.externalId`, `priorityOilRateStd.latestDatapoint.value`).
 *
 * Single source of truth for "is this a dotted path" — every operator that
 * pre-materialises nested columns goes through this so they all agree on
 * what counts as a path.
 */
export function isDottedPath(col: string): boolean {
  return col.includes('.');
}

/**
 * The parent column of a dotted path — the segment up to the first `.`.
 *
 * Mirrors `applySelect`'s flat-first classifier: a dotted path is only
 * resolvable if the parent column exists in the table (the rest of the
 * path is walked through the parent value via {@link getNestedValue}).
 */
export function dottedPathParent(path: string): string {
  const dotIndex = path.indexOf('.');
  return dotIndex === -1 ? path : path.substring(0, dotIndex);
}

/**
 * One pair telling {@link addNestedColumns} which dot-notation path to extract
 * and what flat column name to materialise it under.
 */
export interface NestedColumnSpec {
  /** Output flat column name to attach to the table. */
  key: string;
  /** Dot-notation path resolved row-wise via {@link getNestedValue}. */
  path: string;
}

/**
 * Single-source materialisation primitive: add one or more flat columns to
 * an Arquero table, populated row-wise from dot-notation paths via
 * {@link getNestedValue}.
 *
 * Used by both {@link materializeDottedPaths} (auto-naming with the path
 * itself as the flat column name) and `join.ts::addSyntheticKey` (explicit
 * synthetic keys disjoint from any path). Centralising the round-trip and
 * empty-table handling here keeps the two callers in lockstep — there is
 * no other place in the codebase that knows how to materialise a nested
 * column on an Arquero table.
 *
 * Pragmatic `.objects() → map → aq.from()` round-trip matches the existing
 * `applyDerive` pattern in the executor. Empty tables attach empty column
 * arrays via `aq.table()` so downstream operators (join, dedupe) still see
 * the synthetic columns as real.
 *
 * Caller-owned validation: `addNestedColumns` does not check whether a
 * path's parent exists or whether the target name collides with a flat
 * column — those policies live in higher-level wrappers
 * ({@link materializeDottedPaths} for the spec-walking case,
 * `resolveJoinKey` for the join case).
 */
export function addNestedColumns(table: ColumnTable, specs: readonly NestedColumnSpec[]): ColumnTable {
  if (specs.length === 0) {
    return table;
  }

  if (table.numRows() === 0) {
    const columns: Record<string, unknown[]> = {};
    for (const name of table.columnNames()) {
      columns[name] = [];
    }
    for (const spec of specs) {
      columns[spec.key] = [];
    }
    return aq.table(columns);
  }

  const rows = table.objects() as Record<string, unknown>[];
  const withCols = rows.map((row) => {
    const out: Record<string, unknown> = { ...row };
    for (const spec of specs) {
      // Normalise missing nested values to `null` so Arquero columns are
      // well-formed (Arquero rejects `undefined`).
      out[spec.key] = getNestedValue(row, spec.path) ?? null;
    }
    return out;
  });
  return aq.from(withCols);
}

/**
 * Materialise dotted-path columns as flat columns on an Arquero table.
 *
 * For each path that is (a) a dotted path, (b) whose parent column exists,
 * and (c) is not already a flat column, add a new column named with the
 * full dotted path and populated row-wise via {@link getNestedValue}.
 *
 * After this pass every downstream operator can reference a dotted path
 * as if it were a flat column — sort, distinct, aggregations, reshape,
 * groupBy etc. all become uniform with filter/select/derive (which already
 * resolve dotted paths via {@link getNestedValue} or math.js dot syntax).
 *
 * Delegates to {@link addNestedColumns} after filtering, so the round-trip
 * and empty-table edge stay in one place.
 */
export function materializeDottedPaths(table: ColumnTable, paths: readonly string[]): ColumnTable {
  const existing = new Set(table.columnNames());
  const targets = Array.from(new Set(paths)).filter(
    (p) => isDottedPath(p) && existing.has(dottedPathParent(p)) && !existing.has(p),
  );
  return addNestedColumns(
    table,
    targets.map((path) => ({ key: path, path })),
  );
}

/**
 * Walk a {@link PipelineSpec} and collect every column reference that uses
 * dotted-path notation. Used by {@link transformDataset} to pre-materialise
 * nested columns once, before any action runs.
 *
 * Delegates the spec traversal to {@link collectColumnRefs} — the SSOT
 * walker over column-reference sites. Adding a new action field that takes
 * a column name only requires updating the walker; this collector picks up
 * the new site automatically.
 *
 * Skipped intentionally:
 * - `derive.derives[].name` and `aggregate.aggregations[].name` — those are
 *   output names, not refs. Derive expressions resolve dotted paths
 *   natively through math.js's `row.foo.bar` syntax.
 */
export function collectDottedPathRefs(spec: PipelineSpec): string[] {
  return collectColumnRefs(spec).filter(isDottedPath);
}
